import React, { useMemo } from 'react';
import { useLanguage } from '../../i18n';
import { useCosts } from '../../context/CostsContext';
import { grunderwerbsteuerRates } from '../../data/acquisitionCostConfig';
import { cities } from '../../data/rentalMarketData';
import { entityTypes } from '../../data/taxEntityConfig';
import { calculateAnnualRentalIncome } from '../../data/propertyRevenueModel';
import { heatingTypes } from '../../data/co2Config';
import './FinancialStatements.css';

const energieausweisClasses = ['A+', 'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];

function RevenueInput() {
  const { t } = useLanguage();
  const { costs, updateProperty, updateTaxEntity, updateRevenue, updateProjection, updateVat } = useCosts();

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  // Filter cities by selected Bundesland
  const citiesForBundesland = useMemo(() => {
    const result = {};
    Object.entries(cities).forEach(([key, city]) => {
      if (city.bundesland === costs.property.bundesland) {
        result[key] = city;
      }
    });
    return result;
  }, [costs.property.bundesland]);

  // Year 1 rental income calculation
  const year1Income = useMemo(() => {
    if (costs.revenue.rentalType === 'shortterm') {
      return calculateAnnualRentalIncome({
        rentalType: 'shortterm',
        nightlyRate: costs.revenue.nightlyRate,
        availableNights: costs.revenue.availableNights,
        occupancyRate: costs.revenue.occupancyRate / 100,
        platformFeePercent: costs.revenue.platformFeePercent,
      });
    }
    return calculateAnnualRentalIncome({
      rentalType: 'longterm',
      sqm: costs.property.squareMeters,
      units: costs.property.numberOfUnits,
      kaltmietePerSqm: costs.revenue.kaltmietePerSqm,
      vacancyRate: costs.revenue.vacancyRatePercent / 100,
    });
  }, [costs.revenue, costs.property.squareMeters, costs.property.numberOfUnits]);

  // Handle city change: auto-fill rent suggestion
  const handleCityChange = (cityKey) => {
    updateProperty({ city: cityKey });
    const cityData = cities[cityKey];
    if (cityData) {
      updateProperty({ bundesland: cityData.bundesland });
      updateRevenue({
        kaltmietePerSqm: cityData.kaltmietePerSqm.average,
        mietspiegelRent: cityData.kaltmietePerSqm.average,
        mietpreisbremseActive: cityData.mietpreisbremse,
        kappungsgrenzeRate: cityData.kappungsgrenze,
        vacancyRatePercent: cityData.vacancyRatePercent,
      });
    }
  };

  // Handle Bundesland change: reset city if needed
  const handleBundeslandChange = (bundesland) => {
    updateProperty({ bundesland });
    // Check if current city is still in this Bundesland
    const currentCity = cities[costs.property.city];
    if (!currentCity || currentCity.bundesland !== bundesland) {
      // Find first city in new Bundesland
      const firstCity = Object.entries(cities).find(([, c]) => c.bundesland === bundesland);
      if (firstCity) {
        handleCityChange(firstCity[0]);
      }
    }
  };

  return (
    <div className="revenue-input-section">
      <h3>{t('revenue.title')}</h3>
      <p className="revenue-description">{t('revenue.description')}</p>

      {/* Property & Location Grid */}
      <div className="property-selection-grid">
        <div className="property-selection-group">
          <label>{t('revenue.propertyType')}</label>
          <select
            value={costs.property.propertyType}
            onChange={(e) => updateProperty({ propertyType: e.target.value })}
          >
            <option value="eigentumswohnung">{t('revenue.eigentumswohnung')}</option>
            <option value="einfamilienhaus">{t('revenue.einfamilienhaus')}</option>
            <option value="mehrfamilienhaus">{t('revenue.mehrfamilienhaus')}</option>
          </select>
        </div>

        <div className="property-selection-group">
          <label>{t('revenue.bundesland')}</label>
          <select
            value={costs.property.bundesland}
            onChange={(e) => handleBundeslandChange(e.target.value)}
          >
            {Object.entries(grunderwerbsteuerRates).map(([key, state]) => (
              <option key={key} value={key}>{state.label}</option>
            ))}
          </select>
        </div>

        <div className="property-selection-group">
          <label>{t('revenue.city')}</label>
          <select
            value={costs.property.city}
            onChange={(e) => handleCityChange(e.target.value)}
          >
            {Object.entries(citiesForBundesland).map(([key, city]) => (
              <option key={key} value={key}>{city.labelDE}</option>
            ))}
            {Object.keys(citiesForBundesland).length === 0 && (
              <option value="">{t('revenue.noCityData')}</option>
            )}
          </select>
        </div>

        <div className="property-selection-group">
          <label>{t('revenue.squareMeters')}</label>
          <input
            type="number"
            value={costs.property.squareMeters}
            onChange={(e) => updateProperty({ squareMeters: parseFloat(e.target.value) || 0 })}
            min="10"
            max="10000"
          />
        </div>

        {costs.property.propertyType === 'mehrfamilienhaus' && (
          <div className="property-selection-group">
            <label>{t('revenue.numberOfUnits')}</label>
            <input
              type="number"
              value={costs.property.numberOfUnits}
              onChange={(e) => updateProperty({ numberOfUnits: parseInt(e.target.value) || 1 })}
              min="1"
              max="100"
            />
          </div>
        )}

        <div className="property-selection-group">
          <label>{t('revenue.yearBuilt')}</label>
          <input
            type="number"
            value={costs.property.yearBuilt}
            onChange={(e) => updateProperty({ yearBuilt: parseInt(e.target.value) || 1980 })}
            min="1800"
            max="2026"
          />
        </div>

        <div className="property-selection-group">
          <label>
            {t('revenue.landShare')}
            <span className="tooltip">{t('revenue.landShare.tooltip')}</span>
          </label>
          <input
            type="number"
            value={costs.property.landSharePercent}
            onChange={(e) => updateProperty({ landSharePercent: parseFloat(e.target.value) || 0 })}
            min="0"
            max="100"
            step="5"
          />
        </div>

        <div className="property-selection-group">
          <label>{t('revenue.energieausweis')}</label>
          <select
            value={costs.property.energieausweis}
            onChange={(e) => updateProperty({ energieausweis: e.target.value })}
          >
            {energieausweisClasses.map((cls) => (
              <option key={cls} value={cls}>{cls}</option>
            ))}
          </select>
        </div>

        <div className="property-selection-group">
          <label>{t('revenue.heatingType')}</label>
          <select
            value={costs.property.heatingType}
            onChange={(e) => updateProperty({ heatingType: e.target.value })}
          >
            {Object.entries(heatingTypes).map(([key, ht]) => (
              <option key={key} value={key}>{t(`revenue.heating.${key}`)}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Tax Entity */}
      <div className="seasonal-rates-section" style={{ marginBottom: '16px' }}>
        <div className="seasonal-rates-header" style={{ cursor: 'default' }}>
          <h4>{t('revenue.taxEntity')}</h4>
        </div>
        <div style={{ padding: '12px 16px' }}>
          <div className="property-selection-grid">
            <div className="property-selection-group">
              <label>{t('revenue.entityType')}</label>
              <select
                value={costs.taxEntity.type}
                onChange={(e) => updateTaxEntity({ type: e.target.value })}
              >
                {Object.entries(entityTypes).map(([key, entity]) => (
                  <option key={key} value={key}>{entity.nameDE}</option>
                ))}
              </select>
            </div>

            {(costs.taxEntity.type === 'private' || costs.taxEntity.type === 'gmbh_co_kg') && (
              <div className="property-selection-group">
                <label>{t('revenue.personalTaxRate')}</label>
                <div className="rate-input-wrapper">
                  <input
                    type="number"
                    value={costs.taxEntity.personalTaxRate}
                    onChange={(e) => updateTaxEntity({ personalTaxRate: parseFloat(e.target.value) || 0 })}
                    min="0"
                    max="45"
                    step="1"
                  />
                  <span>%</span>
                </div>
              </div>
            )}

            {costs.taxEntity.type === 'gmbh_co_kg' && (
              <div className="property-selection-group">
                <label>{t('revenue.kommanditeinlage')}</label>
                <div className="rate-input-wrapper">
                  <span className="currency-prefix">&euro;</span>
                  <input
                    type="number"
                    value={costs.taxEntity.kommanditeinlage}
                    onChange={(e) => updateTaxEntity({ kommanditeinlage: parseFloat(e.target.value) || 0 })}
                    min="0"
                    max="1000000"
                    step="1000"
                  />
                </div>
              </div>
            )}

            {(costs.taxEntity.type === 'private' || costs.taxEntity.type === 'gmbh_co_kg') && (
              <div className="property-selection-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="checkbox"
                    checked={costs.taxEntity.kirchensteuerEnabled}
                    onChange={(e) => updateTaxEntity({ kirchensteuerEnabled: e.target.checked })}
                  />
                  {t('revenue.kirchensteuer')}
                </label>
                {costs.taxEntity.kirchensteuerEnabled && (
                  <div className="rate-input-wrapper" style={{ marginTop: '4px' }}>
                    <input
                      type="number"
                      value={(costs.taxEntity.kirchensteuerRate * 100).toFixed(0)}
                      onChange={(e) => updateTaxEntity({ kirchensteuerRate: (parseFloat(e.target.value) || 0) / 100 })}
                      min="0"
                      max="9"
                      step="1"
                    />
                    <span>%</span>
                  </div>
                )}
              </div>
            )}

            {(costs.taxEntity.type === 'private' || costs.taxEntity.type === 'gmbh_co_kg') && (
              <div className="property-selection-group">
                <label>
                  {t('revenue.otherIncome')}
                  <span className="tooltip">{t('revenue.otherIncomeHint')}</span>
                </label>
                <div className="rate-input-wrapper">
                  <span className="currency-prefix">&euro;</span>
                  <input
                    type="number"
                    value={costs.taxEntity.otherAnnualIncome}
                    onChange={(e) => updateTaxEntity({ otherAnnualIncome: parseFloat(e.target.value) || 0 })}
                    min="0"
                    max="1000000"
                    step="1000"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Rental Model */}
      <div className="seasonal-rates-section" style={{ marginBottom: '16px' }}>
        <div className="seasonal-rates-header" style={{ cursor: 'default' }}>
          <h4>{t('revenue.rentalModel')}</h4>
        </div>
        <div style={{ padding: '12px 16px' }}>
          <div className="property-selection-grid" style={{ marginBottom: '16px' }}>
            <div className="property-selection-group">
              <label>{t('revenue.rentalType')}</label>
              <select
                value={costs.revenue.rentalType}
                onChange={(e) => updateRevenue({ rentalType: e.target.value })}
              >
                <option value="longterm">{t('revenue.longterm')}</option>
                <option value="shortterm">{t('revenue.shortterm')}</option>
              </select>
            </div>
          </div>

          {costs.revenue.rentalType === 'longterm' ? (
            <div className="property-selection-grid">
              <div className="property-selection-group">
                <label>{t('revenue.kaltmietePerSqm')}</label>
                <div className="rate-input-wrapper">
                  <span className="currency-prefix">€</span>
                  <input
                    type="number"
                    value={costs.revenue.kaltmietePerSqm}
                    onChange={(e) => updateRevenue({ kaltmietePerSqm: parseFloat(e.target.value) || 0 })}
                    min="0"
                    max="50"
                    step="0.5"
                  />
                  <span>/m²</span>
                </div>
              </div>

              <div className="property-selection-group">
                <label>{t('revenue.vacancyRate')}</label>
                <div className="rate-input-wrapper">
                  <input
                    type="number"
                    value={costs.revenue.vacancyRatePercent}
                    onChange={(e) => updateRevenue({ vacancyRatePercent: parseFloat(e.target.value) || 0 })}
                    min="0"
                    max="30"
                    step="0.5"
                  />
                  <span>%</span>
                </div>
              </div>

              <div className="property-selection-group">
                <label>{t('revenue.rentIncreaseType')}</label>
                <select
                  value={costs.projection.rentIncreaseType}
                  onChange={(e) => updateProjection({ rentIncreaseType: e.target.value })}
                >
                  <option value="regular">{t('revenue.rentRegular')}</option>
                  <option value="indexmiete">{t('revenue.rentIndex')}</option>
                  <option value="staffelmiete">{t('revenue.rentStaffel')}</option>
                </select>
              </div>

              {costs.projection.rentIncreaseType === 'indexmiete' && (
                <div className="property-selection-group">
                  <label>{t('revenue.cpiGrowth')}</label>
                  <div className="rate-input-wrapper">
                    <input
                      type="number"
                      value={costs.projection.cpiGrowthRate}
                      onChange={(e) => updateProjection({ cpiGrowthRate: parseFloat(e.target.value) || 0 })}
                      min="0"
                      max="10"
                      step="0.5"
                    />
                    <span>%</span>
                  </div>
                </div>
              )}

              {costs.projection.rentIncreaseType === 'staffelmiete' && (
                <div className="property-selection-group">
                  <label>{t('revenue.staffelAmount')}</label>
                  <div className="rate-input-wrapper">
                    <span className="currency-prefix">€</span>
                    <input
                      type="number"
                      value={costs.projection.staffelmieteAmount}
                      onChange={(e) => updateProjection({ staffelmieteAmount: parseFloat(e.target.value) || 0 })}
                      min="0"
                      max="500"
                      step="10"
                    />
                    <span>/mo</span>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="property-selection-grid">
              <div className="property-selection-group">
                <label>{t('revenue.nightlyRate')}</label>
                <div className="rate-input-wrapper">
                  <span className="currency-prefix">€</span>
                  <input
                    type="number"
                    value={costs.revenue.nightlyRate}
                    onChange={(e) => updateRevenue({ nightlyRate: parseFloat(e.target.value) || 0 })}
                    min="0"
                    max="1000"
                    step="5"
                  />
                </div>
              </div>

              <div className="property-selection-group">
                <label>{t('revenue.availableNights')}</label>
                <input
                  type="number"
                  value={costs.revenue.availableNights}
                  onChange={(e) => updateRevenue({ availableNights: parseInt(e.target.value) || 0 })}
                  min="0"
                  max="365"
                />
              </div>

              <div className="property-selection-group">
                <label>{t('revenue.occupancyRate')}</label>
                <div className="rate-input-wrapper">
                  <input
                    type="number"
                    value={costs.revenue.occupancyRate}
                    onChange={(e) => updateRevenue({ occupancyRate: parseFloat(e.target.value) || 0 })}
                    min="0"
                    max="100"
                    step="1"
                  />
                  <span>%</span>
                </div>
              </div>

              <div className="property-selection-group">
                <label>{t('revenue.platformFee')}</label>
                <div className="rate-input-wrapper">
                  <input
                    type="number"
                    value={costs.revenue.platformFeePercent}
                    onChange={(e) => updateRevenue({ platformFeePercent: parseFloat(e.target.value) || 0 })}
                    min="0"
                    max="30"
                    step="1"
                  />
                  <span>%</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* VAT section for short-term rental */}
      {costs.revenue.rentalType === 'shortterm' && (
        <div className="seasonal-rates-section" style={{ marginBottom: '16px' }}>
          <div className="seasonal-rates-header" style={{ cursor: 'default' }}>
            <h4>{t('revenue.vatSection')}</h4>
          </div>
          <div style={{ padding: '12px 16px' }}>
            <div className="property-selection-grid">
              <div className="property-selection-group">
                <label>{t('revenue.vatLiable')}</label>
                <select
                  value={costs.vat.vatLiable}
                  onChange={(e) => updateVat({ vatLiable: e.target.value })}
                >
                  <option value="auto">{t('revenue.vatAuto')}</option>
                  <option value="yes">{t('revenue.vatYes')}</option>
                  <option value="no">{t('revenue.vatNo')}</option>
                </select>
              </div>

              <div className="property-selection-group">
                <label>{t('revenue.hotelServices')}</label>
                <select
                  value={costs.vat.hotelServices ? 'yes' : 'no'}
                  onChange={(e) => updateVat({ hotelServices: e.target.value === 'yes' })}
                >
                  <option value="no">{t('revenue.no')}</option>
                  <option value="yes">{t('revenue.yes')}</option>
                </select>
              </div>

              <div className="property-selection-group">
                <label>{t('revenue.eigennutzung')}</label>
                <select
                  value={costs.vat.eigennutzung ? 'yes' : 'no'}
                  onChange={(e) => updateVat({ eigennutzung: e.target.value === 'yes' })}
                >
                  <option value="no">{t('revenue.no')}</option>
                  <option value="yes">{t('revenue.yes')}</option>
                </select>
              </div>
            </div>
            {costs.vat.vatLiable === 'auto' && (
              <div className="info-message" style={{ marginTop: '8px', fontSize: '0.85rem' }}>
                {t('revenue.vatAutoHint')}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Mietpreisbremse indicator */}
      {costs.revenue.rentalType === 'longterm' && costs.revenue.mietpreisbremseActive && (
        <div className="info-message" style={{ marginBottom: '16px' }}>
          {t('revenue.mietpreisbremseActive')}
        </div>
      )}

      {/* Projection Settings */}
      <div className="inflation-section">
        <div className="property-selection-grid">
          <div className="property-selection-group">
            <label>{t('revenue.horizonYears')}</label>
            <input
              type="number"
              value={costs.projection.horizonYears}
              onChange={(e) => updateProjection({ horizonYears: Math.max(1, Math.min(30, parseInt(e.target.value) || 15)) })}
              min="1"
              max="30"
            />
          </div>

          <div className="property-selection-group">
            <label>{t('revenue.inflationRate')}</label>
            <div className="rate-input-wrapper">
              <input
                type="number"
                value={costs.projection.inflationRate}
                onChange={(e) => updateProjection({ inflationRate: parseFloat(e.target.value) || 0 })}
                min="-5"
                max="10"
                step="0.5"
              />
              <span>%</span>
            </div>
          </div>

          <div className="property-selection-group">
            <label>{t('revenue.landAppreciationRate')}</label>
            <div className="rate-input-wrapper">
              <input
                type="number"
                value={costs.projection.landAppreciationRate}
                onChange={(e) => updateProjection({ landAppreciationRate: parseFloat(e.target.value) || 0 })}
                min="-5"
                max="15"
                step="0.5"
              />
              <span>%</span>
            </div>
          </div>

          <div className="property-selection-group">
            <label>{t('revenue.buildingAppreciationRate')}</label>
            <div className="rate-input-wrapper">
              <input
                type="number"
                value={costs.projection.buildingAppreciationRate}
                onChange={(e) => updateProjection({ buildingAppreciationRate: parseFloat(e.target.value) || 0 })}
                min="-5"
                max="15"
                step="0.5"
              />
              <span>%</span>
            </div>
          </div>
        </div>
      </div>

      {/* Revenue Summary */}
      <div className="vat-breakdown-section">
        <h4>{t('revenue.revenueSummary')}</h4>

        {costs.revenue.rentalType === 'longterm' ? (
          <>
            <div className="vat-breakdown-row">
              <span className="vat-breakdown-label">{t('revenue.grossRent')}:</span>
              <span className="vat-breakdown-value">{formatCurrency(year1Income.grossRent)}</span>
            </div>
            <div className="vat-breakdown-row">
              <span className="vat-breakdown-label">{t('revenue.vacancyLoss')} ({costs.revenue.vacancyRatePercent}%):</span>
              <span className="vat-breakdown-value negative">-{formatCurrency(year1Income.vacancyLoss)}</span>
            </div>
          </>
        ) : (
          <>
            <div className="vat-breakdown-row">
              <span className="vat-breakdown-label">{t('revenue.grossBookings')}:</span>
              <span className="vat-breakdown-value">{formatCurrency(year1Income.grossBookings)}</span>
            </div>
            <div className="vat-breakdown-row">
              <span className="vat-breakdown-label">{t('revenue.platformFees')} ({costs.revenue.platformFeePercent}%):</span>
              <span className="vat-breakdown-value negative">-{formatCurrency(year1Income.platformFees)}</span>
            </div>
          </>
        )}

        <div className="vat-breakdown-row total">
          <span className="vat-breakdown-label">
            {t('revenue.netRentalIncome')}:
          </span>
          <span className="vat-breakdown-value highlight">
            {formatCurrency(year1Income.netRent)}
            <span className="vat-note">← {t('revenue.usedInPL')}</span>
          </span>
        </div>

        {costs.revenue.rentalType === 'longterm' && (
          <div className="vat-breakdown-row" style={{ marginTop: '8px' }}>
            <span className="vat-breakdown-label" style={{ fontSize: '0.85rem', color: '#718096' }}>
              {t('revenue.bruttomietrendite')}:
            </span>
            <span className="vat-breakdown-value" style={{ fontSize: '0.85rem', color: '#718096' }}>
              {((year1Income.grossRent / costs.property.purchasePrice) * 100).toFixed(2)}%
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

export default RevenueInput;
